import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Typography, Alert, AlertTitle } from "@mui/material";
import { Ellipsis, FileWarning } from "lucide-react";
import { Document } from "protogen/documents_pb";
import { useGetPublicDocument, useGetDocument } from "services/documents";
import DocumentViewer from "components/editor/DocumentViewer";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import { Attachment } from "protogen/common_pb";
import DropdownMenu from "components/common/DropdownMenu";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import useTitle from "components/hooks/useTitle";
import AuthService from "services/auth";
interface InitialContent {
  content: string;
  attachments: Attachment[];
  contentType?: string;
}

const _Wrapped = ({
  document,
  initialContent,
}: {
  document: Document | undefined;
  initialContent: InitialContent | undefined;
}) => {
  const documentInitialContent =
    initialContent?.contentType === "html"
      ? initialContent.content
      : JSON.parse(initialContent?.content || "");

  const isMobile = useIsMobile();

  return (
    <Box sx={{ marginTop: "8px" }}>
      <Box>
        <Typography
          variant="h3"
          color="text.primary"
          sx={{ marginBottom: isMobile ? "8px" : "12px" }}
        >
          {document?.title}
        </Typography>
      </Box>

      <Box>
        <DocumentViewer
          content={documentInitialContent}
          attachments={initialContent?.attachments}
          minHeight="29px"
          sx={{ padding: "0" }}
        />
      </Box>
    </Box>
  );
};

export default () => {
  const currentUser = AuthService.getCurrentUser();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const docType = searchParams.get("doc_type");
  const isMobile = useIsMobile();
  const [document, setDocument] = useState<Document>();
  const { pushSnack } = useSnackpack();
  const [initialContent, setInitialContent] = useState<InitialContent>();
  useTitle(document?.title);
  const getDocumentFn = currentUser ? useGetDocument : useGetPublicDocument;
  const { request, loading, errorCode } = getDocumentFn((r) => {
    setInitialContent({
      content: r?.document?.note?.content?.payload || "",
      attachments: r?.document?.note?.content?.attachments || [],
      contentType: r?.document?.note?.content?.contentType,
    });
    setDocument(r.document);
  });
  useEffect(() => {
    if (!params.documentRef) return;
    request({
      documentRef: params.documentRef,
      documentType: docType,
      isFormDefinition: false,
    });
  }, [params]);

  const onPrintDocument = () => {
    const printWindow = window.open("", "_blank");
    const content = document?.note?.content?.payload;
    const title = document?.title || "";
    const attachments = document?.note?.content?.attachments;
    if (printWindow) {
      printWindow.document.write(content || "");
      printWindow.document.close();
      printWindow.focus();
      // Set the document title dynamically
      printWindow.document.title = title || "";
      setTimeout(
        () => {
          printWindow.print();
          printWindow.close();
        },
        1000 + attachments.length * 500,
      ); // Wait for a moment for image attachments to load.
    }
  };

  const documentActionButtons = (
    dropdownOptions: {
      title: string;
      onClick: () => void;
      disabled?: boolean;
    }[],
  ) => (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <DropdownMenu
        disabled={false}
        fullWidth
        buttonContent={<Ellipsis size={20} stroke="#198282" />}
        dropdownIcon={false}
        variant="outlined"
        sx={{
          padding: "0 11px",
          border: "1px solid #D4D4D4",
          borderRadius: "12px",
          height: "38px",
          minWidth: "20px",
          "&:hover": {
            border: "1px solid #D4D4D4",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        options={dropdownOptions}
      />
    </Box>
  );
  const copyLinkHandler = () => {
    if (!document?.uuid) return;
    pushSnack({
      message: "Document link copied.",
      alertSeverity: "success",
      key: `document-${document?.ref}`,
    });
    navigator.clipboard.writeText(
      `${window.location.origin}/documents/${document?.uuid}`,
    );
  };

  const additionalOptions = [
    {
      title: "Copy link",
      onClick: () => copyLinkHandler(),
    },
    {
      title: "Print",
      onClick: () => onPrintDocument(),
    },
  ];
  // We need the document before calling the component.
  // The auto save hook takes a callback that needs the document ref.
  const _content = () => {
    if (!document && errorCode === 403) {
      return (
        <Alert
          severity="warning"
          icon={<FileWarning size={20} />}
          sx={{ borderRadius: "10px" }}
        >
          <AlertTitle>
            Sorry, you do not have permission to view this document
          </AlertTitle>
          If you believe this is a mistake, reach out to the owner and request
          that they update the document's settings.
        </Alert>
      );
    } else if (!document || loading) {
      return <Loading />;
    }
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "start" : "center",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {isMobile && documentActionButtons(additionalOptions)}
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: isMobile ? "start" : "center",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "flex-end",
            }}
          >
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {!isMobile && documentActionButtons(additionalOptions)}
            </Box>
          </Box>
        </Box>

        <_Wrapped document={document} initialContent={initialContent} />
      </Box>
    );
  };
  return _content();
};
